import { useEventStore } from '@/state/stores/use-event-store';
import { Button, Menu, MenuItem, Stack } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import AddFlowModal from './tab-actions/add-flow.modal';
import { Add, FilterList } from '@mui/icons-material';
import { useMemo, useState, useCallback } from 'react';
// import Table from './table';
import { FlowGrid } from './grid';
// import { usePreferencesStore } from '@/state/stores/use-preferences-store';
import { getFlowDayNumber } from '@/components/events/helpers/get-flow-day-number.helper';
import useEventMode from '@/components/events/hooks/use-event-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import EmptyState from '@/components/common/empty-state';
import { masterFlowsForEventList } from '@/helpers/flow-scope.helper';

export const FlowsTab = () => {
  const eventFlows = useEventStore((s) => s.eventFlows);
  const event = useEventStore((s) => s.event);
  // const theme = useTheme();
  // const { setPreferences } = usePreferencesStore();
  const [addFlowsOpen, setAddFlowsOpen] = useState(false);
  const t = useTranslations();
  const [selectedDay, setSelectedDay] = useState<string>('all');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const mode = useEventMode();

  const handleDayFilterClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleDaySelect = useCallback((dayId: string) => {
    setSelectedDay(dayId);
    setAnchorEl(null);
  }, []);

  // sort by: day => start time
  // filter by: day
  const sortedFilteredEvents = useMemo(() => {
    return (
      masterFlowsForEventList(eventFlows)
        ?.sort((a, b) => {
          const dayA = getFlowDayNumber(a, event);
          const dayB = getFlowDayNumber(b, event);

          if (dayA !== dayB) {
            if (dayA === null) return 1;
            if (dayB === null) return -1;
            return dayA - dayB;
          }

          const startTimeA = a.slot?.startOffsetMinutes ?? 0;
          const startTimeB = b.slot?.startOffsetMinutes ?? 0;
          return startTimeA - startTimeB;
        })
        .filter((flow) => selectedDay === 'all' || flow.slot?.day.id === selectedDay) ?? []
    );
  }, [eventFlows, event, selectedDay]);

  // useEffect(() => {
  //   if (!isDesktop) {
  //     setPreferences({ flowExpandedView: true });
  //   }
  // }, [isDesktop]);

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={1} sx={{ height: 22 }}>
        {/* <TabActions */}
        {/*   onViewButtonClick={() => setPreferences({ flowExpandedView: !flowExpandedView })} */}
        {/*   isExpandedView={flowExpandedView} */}
        {/* /> */}
        <Button
          startIcon={<FilterList color={selectedDay !== 'all' ? 'primary' : 'info'} />}
          color={selectedDay !== 'all' ? 'primary' : 'info'}
          onClick={handleDayFilterClick}
          aria-controls={open ? 'day-filter-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          data-testid="flow-day-filter-select"
        >
          {selectedDay === 'all'
            ? t('event.detail.dashboard.flows.allDays')
            : (() => {
                const days = event?.days;
                if (!days) return t('event.detail.dashboard.flows.allDays');
                const dayIndex = days.findIndex((day) => day.id === selectedDay);
                return dayIndex !== -1
                  ? t('event.detail.dashboard.flows.day', { dayNumber: (dayIndex + 1).toString() })
                  : t('event.detail.dashboard.flows.allDays');
              })()}
        </Button>
        <Menu
          id="day-filter-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'day-filter-button',
          }}
        >
          <MenuItem onClick={() => handleDaySelect('all')}>{t('event.detail.dashboard.flows.allDays')}</MenuItem>
          {event?.days?.map((day, i) => (
            <MenuItem key={`day-${i}`} onClick={() => handleDaySelect(day.id)}>
              {event?.startDate && t('event.detail.dashboard.flows.day', { dayNumber: (i + 1).toString() })}
            </MenuItem>
          ))}
        </Menu>
        <>
          <Button
            color="info"
            variant="contained"
            disabled={isOrganizeMode(mode)}
            data-testid="add-flow-button"
            onClick={() => setAddFlowsOpen(true)}
            startIcon={<Add />}
          >
            {t('common.new')}
          </Button>
          <AddFlowModal open={addFlowsOpen} setOpen={setAddFlowsOpen} />
        </>
      </Stack>
      {eventFlows?.length ? (
        <FlowGrid flows={sortedFilteredEvents} />
      ) : (
        <EmptyState text={t('flows.noFlows')} variant="compact" />
      )}
    </Stack>
  );
};
